import { useApi } from '@/api';
import { EntityName, Link, TableSkeleton } from '@/components';
import { DATE_MAPPING } from '@/constants';
import {
  Box,
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Flex,
  PercentageValue,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

export default function IlksTable({ daysAgo }) {
  const { sorting } = useTableProps({
    initialSorting: '-debt',
    filtersDebounceTime: 50
  });

  const [data, error] = useApi({
    url: '/groups/osero/ilks/',
    queryParams: {
      order: sorting.currentSorting,
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  const date = DATE_MAPPING[daysAgo];
  if (data) {
    const columns: TableColumn[] = [
      {
        header: '',
        getCell: (row) => (
          <EntityName
            symbol1={
              <CryptoIcon
                name={row.collateral_symbol}
                address={row.collateral_address}
                size="3"
              />
            }>
            <Flex gap="3xs" alignItems="flex-start" flexDirection="column">
              <Flex gap="3xs" alignItems="center">
                {row.name}
              </Flex>
              <Flex gap="3xs" alignItems="center" color="muted" fontSize="-1">
                {row.ilk}
              </Flex>
            </Flex>
          </EntityName>
        ),
        columnSize: ['minmax(100px, auto)', 'minmax(150px, auto)']
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Deployed
            <CryptoIcon name="DAI" size="0" />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.debt} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt'
      },
      {
        header: `Change (${date})`,
        getCell: (row) => (
          <CurrencyValue
            value={row.debt_change}
            options={{
              trend: true,
              iconSize: '0'
            }}
          />
        ),
        getCellBottom: (row) => (
          <PercentageValue
            color="muted"
            fontSize="-1"
            value={row.debt_change_percentage}
            options={{
              nullable: (
                <Box color="muted" fontSize="-1">
                  -
                </Box>
              )
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt_change'
      },
      {
        header: 'Collateral',
        getCell: (row) => (
          <CurrencyValue
            value={row.collateral}
            options={{
              currency: 'USD'
            }}
          />
        ),
        getCellBottom: (row) => (
          <CurrencyValue
            fontSize="-1"
            value={row.collateral_change}
            options={{
              trend: true,
              currency: 'USD',
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'collateral'
      },
      {
        header: 'APY',
        getCell: (row) => <PercentageValue value={row.duty} />,
        getCellBottom: (row) => (
          <PercentageValue
            fontSize="-1"
            value={row.duty_change}
            options={{ trend: true, iconSize: '-1' }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'duty'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.ilk}
        rowLinkProps={{
          to: (row) => row.ilk,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
      />
    );
  }

  return <TableSkeleton />;
}
